/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { type JSX } from 'react';
import ReactDOM from 'react-dom';
import { observable, action, when, type IReactionDisposer, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

type ContextMenuProps = {
  container: React.RefObject<HTMLElement | null>;
  showOnClick?: boolean;
  align?: 'left' | 'right';
  onAppear?: (popupElement: HTMLElement) => boolean;
  onDisappear?: () => void;
  children(popupElement: HTMLElement): JSX.Element | null;
};

// Registry of currently-open menus so that opening one closes all others.
const openMenus = new Set<ContextMenu>();

@observer
export default class ContextMenu extends React.Component<ContextMenuProps> {
  private _contextMenu: React.RefObject<HTMLDivElement | null> = React.createRef();
  private _popupMouseEvent: MouseEvent | null = null;
  private _contextMenuWhenDispower: IReactionDisposer | null = null;
  @observable private _isVisible = false;
  @observable.ref private _popupElement: HTMLElement | null = null;
  @observable private _container: React.RefObject<HTMLElement | null> | null = null;

  constructor(props: ContextMenuProps){
    super(props);
    makeObservable(this);
    this._container = props.container;
    this._contextMenuWhenDispower = when(() => !!(this._container && this._container.current), () => {
      const { container } = this.props;
      if (container.current) container.current.addEventListener('contextmenu', this._onTargetContextMenu);
      if (container.current) container.current.addEventListener('click', this._onTargetClick);
      if (container.current) container.current.addEventListener('scroll', this._onDocumentScroll);
    });
  }

  componentDidMount() {
    document.addEventListener('click', this._onDocumentClick);
    document.addEventListener('scroll', this._onDocumentScroll);
    document.addEventListener('contextmenu', this._onDocumentContextMenu, { capture: true });
    document.addEventListener('keydown', this._onKeydownPress);
    window.addEventListener('resize', this._onDocumentResize);

    // createRef().current is not MobX-observable so the when() in the constructor
    // may never fire. Register container listeners here as a fallback now that
    // all refs are populated after mount.
    const { container } = this.props;
    if (container.current) {
      container.current.addEventListener('contextmenu', this._onTargetContextMenu);
      container.current.addEventListener('click', this._onTargetClick);
      container.current.addEventListener('scroll', this._onDocumentScroll);
      if (this._contextMenuWhenDispower) {
        this._contextMenuWhenDispower();
        this._contextMenuWhenDispower = null;
      }
    }
  }

  componentWillUnmount() {
    openMenus.delete(this);
    const { container } = this.props;
    if (container.current) container.current.removeEventListener('contextmenu', this._onTargetContextMenu);
    if (container.current) container.current.removeEventListener('click', this._onTargetClick);
    if (container.current) container.current.removeEventListener('scroll', this._onDocumentScroll);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('scroll', this._onDocumentScroll);
    document.removeEventListener('contextmenu', this._onDocumentContextMenu, { capture: true });
    document.removeEventListener('keydown', this._onKeydownPress);
    window.removeEventListener('resize', this._onDocumentResize);
    if (this._contextMenuWhenDispower){
      this._contextMenuWhenDispower();
      this._contextMenuWhenDispower = null;
    }
  }

  @action.bound
  private _onDocumentResize() {
    this._hide();
  }

  @action.bound
  private _onDocumentContextMenu(event: MouseEvent) {
    const prevElement = this._popupElement;
    this._popupElement = event.target as HTMLElement;

    if(prevElement !== this._popupElement){
      this._onDocumentScroll();
    }
  }

  @action
  componentDidUpdate(){
    this._container = this.props.container;
    this._updateMenuPosition();
  }

  render(){
    if (!this._isVisible) return null;

    const menu = (
      <div ref={this._contextMenu} className="contextMenu" onContextMenu={(e) => this._onTargetContextMenu(e as unknown as MouseEvent)}>
        {this._popupElement && this.props.children(this._popupElement)}
      </div>
    );

    return ReactDOM.createPortal(menu, document.body);
  }

  @action.bound
  private _onKeydownPress(event: KeyboardEvent){
    if (event.code === 'Escape') {
      this._isVisible = false;
    }
  }

  @action.bound
  private _onTargetClick(event: MouseEvent){
    const menuTarget = (event.target as HTMLElement).closest('.context-menu-target');

    // If a menu is already visible and the user clicks a DIFFERENT trigger,
    // hide this menu first so multiple menus don't stay on screen.
    if (this._isVisible && menuTarget && menuTarget !== this._popupElement) {
      this._hide();
      return;
    }

    if (menuTarget && event.button === 0 /* Left */ && !this.props.showOnClick) {
      event.preventDefault();
      event.stopPropagation();
      this._onTargetContextMenu(event);
    }

    if (this.props.showOnClick){
      this._show(event);
    }
  }
  
  @action.bound
  private _onTargetContextMenu(event: MouseEvent) {
    if (this.props.showOnClick) {
      return;
    }

    event.preventDefault();
    this._show(event);
  }

  @action.bound
  private _show(event: MouseEvent){
    this._popupElement = event.currentTarget as HTMLElement;

    if (this.props.onAppear) {
      if (!this.props.onAppear(this._popupElement)){
        return;
      }
    }

    // Close any other open menus so only one is visible at a time.
    for (const menu of openMenus) {
      if (menu !== this) menu._hide();
    }
    openMenus.add(this);

    this._isVisible = true;
    this._popupMouseEvent = event;

    setTimeout(this._updateMenuPosition, 0);
  }

  @action.bound
  private _onDocumentClick(event: MouseEvent) {
    if (this.props.showOnClick) {
      //check if clicked on container
      const container = this._container?.current;
      if (container && container.contains(event.target as Node)) {
        return;
      }
    }


    const target = event.target as HTMLDivElement;
    if (!target || target.classList.contains('menu-action') || target.classList.contains('with-children')) return;
    
    if (this._isVisible || target !== this._contextMenu.current){
      this._hide();
    }
  }

  @action.bound
  private _hide(){
    openMenus.delete(this);
    this.props.onDisappear?.();
    this._isVisible = false;
  }

  @action.bound
  private _onDocumentScroll() {
    if (this._isVisible) this._hide();
  }

  @action.bound
  private _updateMenuPosition() {
    if (!this._popupMouseEvent) return;
    let clickX = this._popupMouseEvent.clientX;
    let clickY = this._popupMouseEvent.clientY;

    if (this.props.showOnClick){
      // Anchor click-open menus to the trigger's bottom corner.
      // "right" aligns the menu's right edge with the trigger's right edge;
      // "left" (default) aligns the menu's left edge with the trigger's left edge.
      const container = this._container?.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        clickX = this.props.align === 'right' ? rect.right : rect.left;
        clickY = rect.bottom;
      }
    }

    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const contextMenu = this._contextMenu.current;
    if (contextMenu) {
      const rootW = contextMenu.offsetWidth;
      const rootH = contextMenu.offsetHeight;

      const right = (screenW - clickX) > rootW;
      const left = !right;
      const top = (screenH - clickY) > rootH;
      const bottom = !top;

      if (right) {
        contextMenu.style.left = `${clickX + 5}px`;
      }

      if (left) {
        contextMenu.style.left = `${clickX - rootW - 5}px`;
      }

      if (top) {
        contextMenu.style.top = `${clickY + 5}px`;
      }

      if (bottom) {
        contextMenu.style.top = `${clickY - rootH - 5}px`;
      }
    }
  }
}
